import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material'
import moment, { type Moment } from 'moment'
import { useTranslation } from 'react-i18next'

import { type SurveyDefinitionByUserResponse } from '~/api'
import householdBanner from '~/assets/family.png'
import ScreenHeader from '~/components/ScreenHeader'
import MainLayout from '~/components/layouts/MainLayout'
import { useGetSurveyDefinitionByUser } from '~/features/Surveys/hooks/useGetSurveyDefinitionByUser'

import { DynamicLinksFilterPanel } from '../components/DynamicLinksFilterPanel'
import { DynamicLinksSurveyGroup } from '../components/DynamicLinksSurveyGroup'
import { useListDynamicLinks } from '../hooks/useDynamicLinks'
import { DynamicLinkState } from '../types/dynamic-links-states.enum'
import { type SurveyDynamicModel } from '../types/survey-dynamic-model.type'

export function DynamicLinks() {
  const { t } = useTranslation()
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [selectedDateFrom, setSelectedDateFrom] = useState<Moment | null>(() =>
    moment().subtract(30, 'days').startOf('day'),
  )
  const [selectedDateTo, setSelectedDateTo] = useState<Moment | null>(() =>
    moment().endOf('day'),
  )
  const [selectedSurvey, setSelectedSurvey] =
    useState<SurveyDefinitionByUserResponse | null>(null)
  const [isActive, setIsActive] = useState<boolean>(true)
  const [visibleGroups, setVisibleGroups] = useState<number>(5)

  const { data: surveyDefinitionsData } = useGetSurveyDefinitionByUser()
  const surveyDefinitions =
    surveyDefinitionsData?.data?.data?.surveysDefinitionByUser ?? []

  const {
    data: dynamicLinks,
    isLoading,
    isError,
    error,
  } = useListDynamicLinks({
    createdDateFrom: selectedDateFrom?.format('YYYY-MM-DD'),
    createdDateTo: selectedDateTo?.format('YYYY-MM-DD'),
    surveyDefinitionId: selectedSurvey?.id,
  })

  // Check if error is a non-400 error
  const isNon400Error = useMemo(() => {
    return Boolean(
      isError &&
      error &&
      typeof error === 'object' &&
      'status' in error &&
      (error as any).status !== 400,
    )
  }, [isError, error])

  // Group dynamic links by survey definition
  const groupedSurveys = useMemo(() => {
    if (!dynamicLinks) return {}

    const groups: Record<string, SurveyDynamicModel[]> = {}

    dynamicLinks.forEach(link => {
      // Skip links with missing required fields
      if (!link.id || !link.createdDate || !link.createdBy) return

      // Filter by status
      if (isActive && link.status !== DynamicLinkState.ACTIVE) return
      if (!isActive && link.status === DynamicLinkState.ACTIVE) return

      // Filter by user if selected
      if (selectedUser && link.createdBy !== selectedUser) return

      // Filter by survey if selected
      if (selectedSurvey && link.surveyDefinition !== selectedSurvey.id) return

      // Use surveyDefinition as the group name
      const groupName = `Survey ${link.surveyDefinition ?? 'Unknown'}`

      if (!groups[groupName]) {
        groups[groupName] = []
      }

      groups[groupName].push(link)
    })

    return groups
  }, [dynamicLinks, isActive, selectedUser, selectedSurvey])

  function handleShowMore() {
    setVisibleGroups(prev => prev + 5)
  }

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <ScreenHeader
          title={t('views.dynamicLinks.metaTitle')}
          image={householdBanner}
        />
        <DynamicLinksFilterPanel
          selectedUser={selectedUser}
          onUserChange={setSelectedUser}
          selectedDateFrom={selectedDateFrom}
          onDateFromChange={setSelectedDateFrom}
          selectedDateTo={selectedDateTo}
          onDateToChange={setSelectedDateTo}
          selectedSurvey={selectedSurvey}
          onSurveyChange={setSelectedSurvey}
          isActive={isActive}
          onActiveChange={setIsActive}
          surveyDefinitions={surveyDefinitions}
          dynamicLinks={dynamicLinks ?? []}
        />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {isError && (
            <Alert
              severity="info"
              sx={{
                mt: 4,
              }}
            >
              {error instanceof Error &&
              'status' in error &&
              (error as any).status === 400
                ? t('views.dynamicLinks.noDynamicLinksFound')
                : t('views.dynamicLinks.fetchingError')}
            </Alert>
          )}

          {isNon400Error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error instanceof Error
                ? error.message
                : t('views.dynamicLinks.dynamicLinksNotFound')}
            </Alert>
          )}
          {!isLoading &&
            !isError &&
            Object.keys(groupedSurveys).length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {t('views.dynamicLinks.noDynamicLinksFound')}
                </Typography>
              </Box>
            )}

          {!isLoading &&
            !isError &&
            Object.entries(groupedSurveys)
              .slice(0, visibleGroups)
              .map(([groupName, surveys]) => (
                <DynamicLinksSurveyGroup
                  key={groupName}
                  title={groupName}
                  surveys={surveys}
                  surveyDefinitionsByUser={surveyDefinitions}
                />
              ))}
        </Box>

        {!isLoading &&
          !isError &&
          Object.keys(groupedSurveys).length > visibleGroups && (
            <Stack>
              <Button
                variant="text"
                color="success"
                size="medium"
                sx={{ alignSelf: 'center' }}
                onClick={handleShowMore}
              >
                {t('views.support.viewmore')}
              </Button>
            </Stack>
          )}
      </Container>
    </MainLayout>
  )
}
